import { spellOutNumber, spellOutNumberFeminine } from './spanishNumbers';

/**
 * Construcción de texto de la "Constancia de Atención" — separado del render
 * jsPDF (`constanciaPdfTemplate.ts`) a propósito: es lógica pura (sin
 * dependencia de jsPDF/DOM), así se puede testear sin arrastrar el entorno
 * de canvas/TextEncoder que jsPDF necesita.
 */

/** Texto idéntico al de psima — no es informe clínico, solo registro administrativo. */
export const CONSTANCIA_DISCLAIMER =
  'El presente documento no constituye un informe clínico, diagnóstico ni evaluación psicológica, y no acredita el contenido del proceso terapéutico. Los datos consignados reflejan exclusivamente registros administrativos de la plataforma.';

export interface ConstanciaData {
  patientFullName: string;
  psychologistName: string;
  psychologistSurname: string;
  /** Cantidad de sesiones efectivas (`settled`/`free`) del proceso. */
  sessionCount: number;
  /** Fecha ISO de la sesión efectiva más antigua. */
  firstSessionDate: string;
  /** Fecha ISO de la sesión efectiva más reciente. */
  lastSessionDate: string;
}

/** Un run de texto con estilo uniforme — building block del párrafo principal. */
export interface Run {
  text: string;
  bold?: boolean;
}

export function formatDateLong(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDateShort(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getFullYear()}`;
}

/** Arma los runs del cuerpo, idéntico a las oraciones de `ConstanciaAtencionPdfDocument` (psima). */
export function buildConstanciaBodyRuns(data: ConstanciaData): Run[] {
  const { patientFullName, psychologistName, psychologistSurname, sessionCount, firstSessionDate, lastSessionDate } = data;
  const sessionsNoun = sessionCount === 1 ? 'sesión' : 'sesiones';
  const sessionCountWords = sessionCount === 1 ? spellOutNumberFeminine(sessionCount) : spellOutNumber(sessionCount);
  // psima conjuga "mantuvo"/"mantuvieron" según la cantidad de pacientes del proceso
  // (pareja/familia). psi-platform hoy solo expone un `patientFullName` (string único,
  // sin el array de pacientes por proceso) — se deja fijo en singular hasta que el
  // dato de pacientes múltiples por proceso esté disponible acá.
  const mantuvoVerb = 'mantuvo';

  // El espaciado entre runs está escrito a mano (con espacios al final/principio de
  // cada string) para que al concatenarlos quede idéntico al texto plano que arma
  // psima — incluida la coma pegada sin espacio después del nombre de la psicóloga.
  const runs: Run[] = [
    { text: 'Por medio de la presente, se deja constancia de que ' },
    { text: patientFullName, bold: true },
    { text: ` ${mantuvoVerb} ` },
    { text: `${sessionCountWords} (${sessionCount})`, bold: true },
    { text: ` ${sessionsNoun} de psicoterapia junto a ` },
    { text: `${psychologistName} ${psychologistSurname}`, bold: true },
    { text: ', profesional a cargo, conforme surge de los registros de la plataforma Psi Mammoliti, ' },
  ];

  if (sessionCount === 1) {
    runs.push({ text: 'en la fecha del ' });
    runs.push({ text: `${formatDateLong(firstSessionDate)}.`, bold: true });
  } else {
    runs.push({ text: 'durante el período comprendido entre el ' });
    runs.push({ text: formatDateLong(firstSessionDate), bold: true });
    runs.push({ text: ' y el ' });
    runs.push({ text: `${formatDateLong(lastSessionDate)}.`, bold: true });
  }

  return runs;
}

/**
 * Tokeniza runs en palabras individuales (cada una con su propio flag de negrita).
 * Concatena todo en un solo string antes de partir por espacios — así una
 * puntuación pegada sin espacio al final de un run (ej. la coma después del
 * nombre de la psicóloga, en un run distinto y sin negrita) queda en la MISMA
 * palabra que el texto anterior, en vez de generar un espacio de más al justificar.
 */
export function tokenizeRuns(runs: Run[]): { text: string; bold: boolean }[] {
  let full = '';
  const boldRanges: { start: number; end: number }[] = [];
  for (const run of runs) {
    const start = full.length;
    full += run.text;
    if (run.bold) boldRanges.push({ start, end: full.length });
  }
  const isBoldAt = (pos: number) => boldRanges.some((r) => pos >= r.start && pos < r.end);

  const words: { text: string; bold: boolean }[] = [];
  const re = /\S+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(full))) {
    words.push({ text: m[0], bold: isBoldAt(m.index) });
  }
  return words;
}
